
export interface BusinessReview {
  id: number;
  author: string;
  avatar: string;
  rating: number;
  date: string;
  text: string;
  helpful: number;
  verified: boolean;
  ownerReply?: string;
}

export interface BusinessPhoto {
  id: number;
  url: string;
  alt: string;
}

export interface BusinessService {
  name: string;
  description: string;
  price?: string;
}

export interface BusinessAttribute {
  icon: string;
  label: string;
}

export interface BusinessHours {
  day: string;
  hours: string;
  isToday?: boolean;
}

export interface BusinessDetail {
  id: number;
  name: string;
  tagline: string;
  category: string;
  categoryLabel: string;
  description: string;
  longDescription: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  image: string;
  logo: string;
  rating: number;
  reviews: number;
  featured: boolean;
  verified: boolean;
  offer: string;
  openNow: boolean;
  priceRange: string;
  yearEstablished: number;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
  attributes: BusinessAttribute[];
  weeklyHours: BusinessHours[];
  photos: BusinessPhoto[];
  services: { category: string; items: BusinessService[] }[];
  reviewsList: BusinessReview[];
}

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const todayIndex = new Date().getDay();
const todayName = days[todayIndex];

export const businessDetails: Record<number, BusinessDetail> = {
  1: {
    id: 1,
    name: 'Roseville Family Dental',
    tagline: 'Your smile is our passion',
    category: 'health',
    categoryLabel: 'Health & Wellness',
    description: 'Comprehensive family dentistry serving Roseville for over 20 years.',
    longDescription:
      'Roseville Family Dental has been a cornerstone of the Roseville community since 2003, providing comprehensive dental care for patients of all ages. Our team of experienced dentists and hygienists is committed to delivering gentle, personalized care in a warm and welcoming environment. We utilize the latest dental technology including digital X-rays, intraoral cameras, and laser dentistry to ensure the most comfortable and effective treatments. Whether you need a routine cleaning, cosmetic enhancement, or restorative procedure, we treat every patient like family. Our office features a relaxing atmosphere with amenities designed to put even the most anxious patients at ease.',
    address: '1234 Douglas Blvd, Roseville, CA 95678',
    phone: '(916) 555-0142',
    email: 'info@rosevillefamilydental.com',
    website: 'https://rosevillefamilydental.com',
    image:
      'https://public.readdy.ai/ai/img_res/33df4a6ff096efc93767bef3b80ec577.jpg',
    logo: 'ri-tooth-line',
    rating: 4.9,
    reviews: 312,
    featured: true,
    verified: true,
    offer: '20% Off First Visit',
    openNow: true,
    priceRange: '$$',
    yearEstablished: 2003,
    socialMedia: {
      facebook: 'https://facebook.com/rosevillefamilydental',
      instagram: 'https://instagram.com/rosevillefamilydental',
    },
    attributes: [
      { icon: 'ri-wheelchair-line', label: 'Wheelchair Accessible' },
      { icon: 'ri-wifi-line', label: 'Free WiFi' },
      { icon: 'ri-parking-box-line', label: 'Free Parking' },
      { icon: 'ri-bank-card-line', label: 'Accepts Insurance' },
      { icon: 'ri-calendar-check-line', label: 'Online Booking' },
      { icon: 'ri-translate-2', label: 'Spanish Speaking' },
    ],
    weeklyHours: [
      { day: 'Monday', hours: '8:00 AM – 6:00 PM', isToday: todayName === 'Monday' },
      { day: 'Tuesday', hours: '8:00 AM – 6:00 PM', isToday: todayName === 'Tuesday' },
      { day: 'Wednesday', hours: '8:00 AM – 6:00 PM', isToday: todayName === 'Wednesday' },
      { day: 'Thursday', hours: '8:00 AM – 6:00 PM', isToday: todayName === 'Thursday' },
      { day: 'Friday', hours: '8:00 AM – 5:00 PM', isToday: todayName === 'Friday' },
      { day: 'Saturday', hours: '9:00 AM – 2:00 PM', isToday: todayName === 'Saturday' },
      { day: 'Sunday', hours: 'Closed', isToday: todayName === 'Sunday' },
    ],
    photos: [
      {
        id: 1,
        url: 'https://readdy.ai/api/search-image?query=modern%20dental%20office%20reception%20area%20with%20comfortable%20seating%20area%20warm%20lighting%20clean%20white%20interior%20with%20teal%20accents%20welcoming%20healthcare%20space&width=600&height=400&seq=bdp1&orientation=landscape',
        alt: 'Reception area',
      },
      {
        id: 2,
        url: 'https://readdy.ai/api/search-image?query=dental%20treatment%20room%20with%20modern%20equipment%20dental%20chair%20monitors%20and%20bright%20overhead%20lighting%20clean%20sterile%20professional%20healthcare%20environment&width=400&height=400&seq=bdp2&orientation=squarish',
        alt: 'Treatment room',
      },
      {
        id: 3,
        url: 'https://readdy.ai/api/search-image?query=dental%20office%20waiting%20room%20with%20magazines%20comfortable%20chairs%20and%20children%20play%20area%20family%20friendly%20healthcare%20space%20natural%20light&width=400&height=400&seq=bdp3&orientation=squarish',
        alt: 'Waiting room',
      },
      {
        id: 4,
        url: 'https://readdy.ai/api/search-image?query=professional%20dental%20team%20group%20photo%20smiling%20dentists%20and%20hygienists%20in%20white%20coats%20standing%20in%20modern%20dental%20office%20friendly%20healthcare%20professionals&width=600&height=400&seq=bdp4&orientation=landscape',
        alt: 'Our team',
      },
      {
        id: 5,
        url: 'https://readdy.ai/api/search-image?query=modern%20dental%20xray%20and%20imaging%20room%20with%20digital%20equipment%20screens%20showing%20dental%20scans%20advanced%20dental%20technology%20clean%20medical%20environment&width=400&height=400&seq=bdp5&orientation=squarish',
        alt: 'Digital imaging',
      },
    ],
    services: [
      {
        category: 'Preventive Care',
        items: [
          {
            name: 'Dental Cleaning & Exam',
            description: 'Comprehensive cleaning, exam, and digital X-rays',
            price: '$150',
          },
          {
            name: 'Fluoride Treatment',
            description: 'Professional fluoride application for cavity prevention',
            price: '$35',
          },
          {
            name: 'Dental Sealants',
            description: 'Protective coating for molars to prevent decay',
            price: '$45/tooth',
          },
        ],
      },
      {
        category: 'Cosmetic Dentistry',
        items: [
          {
            name: 'Teeth Whitening',
            description: 'In-office professional whitening treatment',
            price: '$350',
          },
          {
            name: 'Porcelain Veneers',
            description: 'Custom-crafted veneers for a perfect smile',
            price: '$1,200/tooth',
          },
          {
            name: 'Dental Bonding',
            description: 'Tooth-colored resin to repair chips and gaps',
            price: '$250/tooth',
          },
        ],
      },
      {
        category: 'Restorative',
        items: [
          {
            name: 'Dental Crowns',
            description: 'Porcelain or ceramic crowns for damaged teeth',
            price: '$900',
          },
          {
            name: 'Dental Implants',
            description: 'Permanent tooth replacement with titanium implant',
            price: '$3,500',
          },
          {
            name: 'Root Canal Therapy',
            description: 'Treatment to save infected or damaged teeth',
            price: '$800',
          },
        ],
      },
    ],
    reviewsList: [
      {
        id: 1,
        author: 'Sarah M.',
        avatar: 'SM',
        rating: 5,
        date: '2 weeks ago',
        text: 'Dr. Chen and the entire team are absolutely wonderful. I have been coming here for 5 years and every visit is pleasant. They are thorough, gentle, and always explain everything clearly. The office is spotless and modern. Highly recommend for families!',
        helpful: 24,
        verified: true,
        ownerReply:
          'Thank you so much, Sarah! We love having you and your family as patients. See you at your next visit!',
      },
      {
        id: 2,
        author: 'Michael R.',
        avatar: 'MR',
        rating: 5,
        date: '1 month ago',
        text: 'Had a dental emergency and they fit me in the same day. The staff was incredibly compassionate and professional. The procedure was painless and they followed up the next day to check on me. This is what healthcare should be.',
        helpful: 18,
        verified: true,
      },
      {
        id: 3,
        author: 'Jennifer L.',
        avatar: 'JL',
        rating: 4,
        date: '1 month ago',
        text: 'Great dental office with friendly staff. Wait times can be a bit long during peak hours, but the quality of care more than makes up for it. Love the new digital X-ray technology.',
        helpful: 12,
        verified: true,
      },
      {
        id: 4,
        author: 'David K.',
        avatar: 'DK',
        rating: 5,
        date: '2 months ago',
        text: 'Best dental experience I have ever had. The teeth whitening results were amazing and the price was very reasonable compared to other offices in the area. The whole process took about an hour.',
        helpful: 9,
        verified: false,
      },
      {
        id: 5,
        author: 'Amanda T.',
        avatar: 'AT',
        rating: 5,
        date: '3 months ago',
        // Properly escaped apostrophe in the review text
        text: 'My kids actually look forward to going to the dentist now! The children\'s area is great and the hygienists are so patient and kind with little ones. We switched from another practice and wish we had done it sooner.',
        helpful: 15,
        verified: true,
      },
    ],
  },
  2: {
    id: 2,
    name: 'Sierra Auto Works',
    tagline: 'Trusted auto repair since 2005',
    category: 'auto',
    categoryLabel: 'Automotive',
    description:
      'Full-service auto repair and maintenance shop specializing in both domestic and import vehicles.',
    longDescription:
      "Sierra Auto Works has been Roseville's most trusted auto repair facility since 2005. Our ASE-certified technicians specialize in both domestic and import vehicles, providing everything from routine oil changes to complex engine diagnostics and repairs. We pride ourselves on honest, transparent pricing with no hidden fees. Every repair comes with a detailed explanation and written estimate before any work begins. Our state-of-the-art facility features the latest diagnostic equipment, a comfortable waiting area with complimentary coffee and WiFi, and a shuttle service for your convenience. We stand behind every repair with our 24-month/24,000-mile warranty.",
    address: '890 Riverside Ave, Roseville, CA 95678',
    phone: '(916) 555-0198',
    email: 'service@sierraautoworks.com',
    website: 'https://sierraautoworks.com',
    image:
      'https://readdy.ai/api/search-image?query=modern%20auto%20repair%20shop%20exterior%20with%20clean%20professional%20look%20well%20organized%20garage%20bays%20with%20cars%20being%20serviced%20bright%20signage%20and%20tidy%20parking%20lot%20suburban%20California%20setting&width=1200&height=500&seq=bd2&orientation=landscape',
    logo: 'ri-car-washing-line',
    rating: 4.8,
    reviews: 187,
    featured: true,
    verified: true,
    offer: 'Free Brake Inspection',
    openNow: true,
    priceRange: '$$',
    yearEstablished: 2005,
    socialMedia: {
      facebook: 'https://facebook.com/sierraautoworks',
      instagram: 'https://instagram.com/sierraautoworks',
    },
    attributes: [
      { icon: 'ri-parking-box-line', label: 'Free Parking' },
      { icon: 'ri-wifi-line', label: 'Free WiFi' },
      { icon: 'ri-cup-line', label: 'Free Coffee' },
      { icon: 'ri-taxi-line', label: 'Shuttle Service' },
      { icon: 'ri-shield-check-line', label: '24-Month Warranty' },
      { icon: 'ri-bank-card-line', label: 'Financing Available' },
    ],
    weeklyHours: [
      { day: 'Monday', hours: '7:00 AM – 6:00 PM', isToday: todayName === 'Monday' },
      { day: 'Tuesday', hours: '7:00 AM – 6:00 PM', isToday: todayName === 'Tuesday' },
      { day: 'Wednesday', hours: '7:00 AM – 6:00 PM', isToday: todayName === 'Wednesday' },
      { day: 'Thursday', hours: '7:00 AM – 6:00 PM', isToday: todayName === 'Thursday' },
      { day: 'Friday', hours: '7:00 AM – 6:00 PM', isToday: todayName === 'Friday' },
      { day: 'Saturday', hours: '8:00 AM – 4:00 PM', isToday: todayName === 'Saturday' },
      { day: 'Sunday', hours: 'Closed', isToday: todayName === 'Sunday' },
    ],
    photos: [
      {
        id: 1,
        url: 'https://readdy.ai/api/search-image?query=clean%20organized%20auto%20repair%20garage%20bay%20with%20car%20on%20lift%20professional%20mechanic%20working%20bright%20fluorescent%20lighting%20modern%20automotive%20service%20center&width=600&height=400&seq=bdp6&orientation=landscape',
        alt: 'Service bay',
      },
      {
        id: 2,
        url: 'https://readdy.ai/api/search-image?query=auto%20repair%20shop%20waiting%20room%20with%20comfortable%20seating%20coffee%20machine%20television%20and%20magazines%20clean%20modern%20customer%20lounge%20area&width=400&height=400&seq=bdp7&orientation=squarish',
        alt: 'Customer lounge',
      },
      {
        id: 3,
        url: 'https://readdy.ai/api/search-image?query=automotive%20diagnostic%20equipment%20computer%20screen%20showing%20engine%20diagnostics%20modern%20car%20repair%20technology%20professional%20mechanic%20tools%20organized%20workspace&width=400&height=400&seq=bdp8&orientation=squarish',
        alt: 'Diagnostics',
      },
      {
        id: 4,
        url: 'https://readdy.ai/api/search-image?query=professional%20auto%20mechanic%20team%20group%20photo%20standing%20in%20front%20of%20repair%20shop%20wearing%20uniforms%20friendly%20smiling%20automotive%20technicians&width=600&height=400&seq=bdp9&orientation=landscape',
        alt: 'Our mechanics',
      },
    ],
    services: [
      {
        category: 'Maintenance',
        items: [
          {
            name: 'Oil Change (Synthetic)',
            description: 'Full synthetic oil, filter, and multi-point inspection',
            price: '$69',
          },
          {
            name: 'Brake Service',
            description: 'Pad replacement, rotor inspection, and fluid check',
            price: '$189+',
          },
          {
            name: 'Tire Rotation & Balance',
            description: 'Rotate all four tires and balance for even wear',
            price: '$49',
          },
        ],
      },
      {
        category: 'Diagnostics & Repair',
        items: [
          {
            name: 'Check Engine Light',
            description: 'Full computer diagnostic scan and report',
            price: '$99',
          },
          {
            name: 'AC Service & Repair',
            description: 'System inspection, recharge, and leak detection',
            price: '$149+',
          },
          {
            name: 'Transmission Service',
            description: 'Fluid flush, filter replacement, and inspection',
            price: '$199+',
          },
        ],
      },
    ],
    reviewsList: [
      {
        id: 1,
        author: 'Tom B.',
        avatar: 'TB',
        rating: 5,
        date: '1 week ago',
        text: 'Honest and reliable. They diagnosed my issue quickly and the repair cost was exactly what they quoted. The shuttle service is a great perk. Will definitely be back for all my car needs.',
        helpful: 14,
        verified: true,
        ownerReply: 'Thanks Tom! We appreciate your trust in us. Drive safe!',
      },
      {
        id: 2,
        author: 'Lisa P.',
        avatar: 'LP',
        rating: 5,
        date: '3 weeks ago',
        text: "Finally found a mechanic I can trust! They showed me exactly what was wrong with my car and didn't try to upsell unnecessary services. Fair prices and excellent work.",
        helpful: 21,
        verified: true,
      },
      {
        id: 3,
        author: 'Carlos G.',
        avatar: 'CG',
        rating: 4,
        date: '1 month ago',
        text: 'Good quality work and fair pricing. Took a bit longer than expected for my brake job but they kept me updated throughout. The waiting area is comfortable with free coffee.',
        helpful: 8,
        verified: true,
      },
    ],
  },
};

export const getBusinessDetail = (id: number): BusinessDetail | undefined => {
  return businessDetails[id];
};
